/*
 * quick-shop.js — lightweight, self-contained Quick Shop modal for product cards.
 * Dawn's native quick-add-modal depends on global.js (absent in this theme), so
 * this is a standalone implementation: it fetches /products/<handle>.js, shows
 * the product image/price/variant, and offers "Add to Bag" (AJAX /cart/add.js)
 * and "View Full Details" (PDP link) — no full-page navigation.
 *
 * Trigger: any element with [data-quick-shop-handle="<handle>"].
 */
(function () {
  var modal = null;
  var lastTrigger = null;

  function money(cents) {
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function build() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'quick-shop-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML =
      '<div class="quick-shop-modal__overlay" data-qs-close></div>' +
      '<div class="quick-shop-modal__panel" role="dialog" aria-modal="true" aria-label="Quick shop">' +
      '<button type="button" class="quick-shop-modal__close" aria-label="Close" data-qs-close>&times;</button>' +
      '<div class="quick-shop-modal__body"></div>' +
      '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-qs-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
    return modal;
  }

  function open() {
    build();
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function load(handle) {
    open();
    var body = modal.querySelector('.quick-shop-modal__body');
    body.innerHTML = '<div class="quick-shop-modal__loading">Loading…</div>';
    fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('fetch failed');
        return r.json();
      })
      .then(render)
      .catch(function () {
        body.innerHTML =
          '<div class="quick-shop-modal__loading">Couldn’t load this product. ' +
          '<a href="/products/' + encodeURIComponent(handle) + '">View full details</a></div>';
      });
  }

  function render(p) {
    var body = modal.querySelector('.quick-shop-modal__body');
    var variants = p.variants || [];
    var firstAvail = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) { firstAvail = variants[i]; break; }
    }
    var current = firstAvail || variants[0] || {};
    var img = p.featured_image || (p.images && p.images[0]) || '';

    var optionsHtml = '';
    if (variants.length > 1) {
      optionsHtml =
        '<label class="quick-shop-modal__field"><span>' + escapeHtml(p.options && p.options[0] ? p.options[0] : 'Option') + '</span>' +
        '<select class="quick-shop-modal__variant">' +
        variants.map(function (v) {
          return '<option value="' + v.id + '"' + (v.available ? '' : ' disabled') +
            (v.id === current.id ? ' selected' : '') + '>' +
            escapeHtml(v.title) + (v.available ? '' : ' — Sold out') + '</option>';
        }).join('') +
        '</select></label>';
    }

    body.innerHTML =
      '<div class="quick-shop-modal__media">' + (img ? '<img src="' + img + '" alt="' + escapeHtml(p.title) + '">' : '') + '</div>' +
      '<div class="quick-shop-modal__info">' +
      (p.vendor ? '<p class="quick-shop-modal__vendor">' + escapeHtml(p.vendor) + '</p>' : '') +
      '<h3 class="quick-shop-modal__title">' + escapeHtml(p.title) + '</h3>' +
      '<p class="quick-shop-modal__price">' + money(current.price != null ? current.price : p.price) + '</p>' +
      optionsHtml +
      '<button type="button" class="quick-shop-modal__add button" data-qs-add data-variant="' + (current.id || '') + '"' +
      (p.available ? '' : ' disabled') + '>' + (p.available ? 'Add to Bag' : 'Sold out') + '</button>' +
      '<a class="quick-shop-modal__details" href="' + p.url + '">View Full Details</a>' +
      '<p class="quick-shop-modal__msg" role="status" hidden></p>' +
      '</div>';

    var sel = body.querySelector('.quick-shop-modal__variant');
    var addBtn = body.querySelector('[data-qs-add]');
    var priceEl = body.querySelector('.quick-shop-modal__price');

    if (sel) {
      sel.addEventListener('change', function () {
        var v = null;
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === sel.value) { v = variants[i]; break; }
        }
        if (!v) return;
        addBtn.dataset.variant = v.id;
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? 'Add to Bag' : 'Sold out';
        priceEl.textContent = money(v.price);
      });
    }
    if (addBtn) {
      addBtn.addEventListener('click', function () { addToCart(addBtn); });
    }
    var closeBtn = modal.querySelector('.quick-shop-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function addToCart(addBtn) {
    var body = modal.querySelector('.quick-shop-modal__body');
    var msg = body.querySelector('.quick-shop-modal__msg');
    var id = addBtn.dataset.variant;
    if (!id) return;
    addBtn.disabled = true;
    var label = addBtn.textContent;
    addBtn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function () {
        msg.hidden = false;
        msg.textContent = 'Added to your bag.';
        addBtn.textContent = 'Added ✓';
        document.dispatchEvent(new CustomEvent('quick-shop:added'));
      })
      .catch(function () {
        msg.hidden = false;
        msg.textContent = 'Sorry, this couldn’t be added to your bag.';
        addBtn.disabled = false;
        addBtn.textContent = label;
      });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-shop-handle]');
    if (!trigger) return;
    e.preventDefault();
    lastTrigger = trigger;
    load(trigger.getAttribute('data-quick-shop-handle'));
  });
})();
